import { Component, Input, OnInit } from '@angular/core';
import { SingleValueData } from 'src/app/models/dashboard.model';
import { calculatePercentage, formatPieChartValue, getChartColorClass, getChartTextColorClass, getChartHexColor } from 'src/app/utils/format.utils';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-pie-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss']
})
export class PieChartComponent implements OnInit {
  @Input() data: SingleValueData[] = [];
  @Input() title: string = '';
  @Input() showLegend: boolean = true;
  @Input() animated: boolean = true;
  @Input() showTotal: boolean = true;
  
  total: number = 0;
  pieSegments: { 
    name: string; 
    value: number; 
    color: string; 
    strokeColor: string; 
    textColor: string; 
    percentage: number; 
    path: string;
  }[] = [];
  
  // Centro do círculo SVG
  private readonly centerX: number = 50;
  private readonly centerY: number = 50;
  // Raio do círculo
  private readonly radius: number = 45;
  
  ngOnInit(): void {
    this.processData();
  }
  
  /**
   * Processa os dados quando mudam
   */
  ngOnChanges(): void {
    this.processData();
  }
  
  /**
   * Processa os dados para o gráfico de pizza
   */
  processData(): void {
    if (!this.data || this.data.length === 0) {
      this.total = 0;
      this.pieSegments = [];
      return;
    }
    
    // Calcula o total
    this.total = this.data.reduce((sum, item) => sum + (item.value || 0), 0);
    
    // Ângulo acumulado (começa do topo, -90 graus)
    let cumulativeAngle = -90;
    
    // Processa cada segmento
    this.pieSegments = this.data.map((item, index) => {
      const percentage = this.total > 0 ? (item.value / this.total) * 100 : 0;
      
      // Calcula o ângulo para este segmento (em graus)
      const angle = (percentage / 100) * 360;
      
      // Calcula o caminho SVG para este segmento
      const path = this.calculateSegmentPath(cumulativeAngle, cumulativeAngle + angle);
      
      // Atualiza o ângulo cumulativo para o próximo segmento
      cumulativeAngle += angle;
      
      return {
        name: item.name,
        value: item.value,
        color: getChartColorClass(index),
        strokeColor: getChartHexColor(index),
        textColor: getChartTextColorClass(index),
        percentage: percentage,
        path: path
      };
    });
  }
  
  /**
   * Calcula o caminho SVG para um segmento de gráfico de pizza
   * @param startAngle Ângulo inicial em graus
   * @param endAngle Ângulo final em graus
   * @returns String de caminho SVG para desenhar o segmento
   */
  private calculateSegmentPath(startAngle: number, endAngle: number): string {
    // Converte ângulos de graus para radianos
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    
    // Calcula os pontos do arco
    const startX = this.centerX + this.radius * Math.cos(startRad);
    const startY = this.centerY + this.radius * Math.sin(startRad);
    const endX = this.centerX + this.radius * Math.cos(endRad);
    const endY = this.centerY + this.radius * Math.sin(endRad);
    
    // Flag de arco grande - 1 se o ângulo > 180 graus
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
    
    // Constrói o caminho SVG:
    // M x y - Move para (x,y)
    // L x y - Linha para (x,y)
    // A rx ry x-axis-rotation large-arc-flag sweep-flag x y - Arco para (x,y)
    // Z - Fecha o caminho
    return `
      M ${this.centerX} ${this.centerY}
      L ${startX} ${startY}
      A ${this.radius} ${this.radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
      Z
    `;
  }
  
  /**
   * Formata o valor para exibição
   */
  formatValue(value: number): string {
    return formatPieChartValue(value);
  }
  
  /**
   * Formata o percentual
   */
  formatPercentage(value: number): string {
    return calculatePercentage(value, this.total);
  }
} 